/** What Add machine offers to download: GET /api/fleet/downloads, or demo data in ?mock=1. */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AgentDownloads } from '@orgus/protocol';
import { ApiError, api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { mockDownloads } from './download-model.ts';
import type { AddMachinePreset } from './pairing.ts';

export type DownloadsState = { kind: 'loading' } | { kind: 'error'; error: unknown } | { kind: 'ready'; data: AgentDownloads };

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function load(preset: AddMachinePreset | undefined, signal: AbortSignal): Promise<AgentDownloads> {
  if (useOrgus.getState().mode !== 'mock') return api<AgentDownloads>('/api/fleet/downloads', { signal, timeoutMs: 15_000 });
  await delay(250);
  if (preset === 'error') {
    throw new ApiError({ what: 'Could not load the downloads.', why: 'The server did not answer within 15 s.', next: 'Check your connection, then try again.' }, 504);
  }
  return mockDownloads({ unavailable: preset === 'unavailable', local: preset === 'local' });
}

export function useDownloads(open: boolean, preset?: AddMachinePreset): { state: DownloadsState; retry: () => Promise<void> } {
  const [state, setState] = useState<DownloadsState>({ kind: 'loading' });
  const ctrl = useRef<AbortController | null>(null);

  const fetchNow = useCallback(async () => {
    ctrl.current?.abort();
    const c = new AbortController();
    ctrl.current = c;
    setState({ kind: 'loading' });
    try {
      const data = await load(preset, c.signal);
      if (!c.signal.aborted) setState({ kind: 'ready', data });
    } catch (error) {
      if (!c.signal.aborted) setState({ kind: 'error', error });
    }
  }, [preset]);

  useEffect(() => {
    if (open) void fetchNow();
    return () => ctrl.current?.abort();
  }, [open, fetchNow]);

  return { state, retry: fetchNow };
}
